/**
 * Demo bots for the hero "talk to it" panel.
 *
 * These are four sample sites, not real Common Ninja customers. Every answer
 * below is written by hand and served from this file, so the demo costs
 * nothing per visitor and never fails. It exists to show the SHAPE of what the
 * widget does (answers drawn from a site's own content, product cards inline,
 * a booking handoff, a dead-end reply) rather than to make any claim about a
 * real business.
 *
 * The "paste your own URL" version of this demo is the follow-up: same panel,
 * real crawl, rate-limited and cached per domain.
 */

export interface IDemoProductCard {
  title: string;
  price: string;
  meta: string;
  /** One of the inline thumbnail glyphs drawn in the component. */
  glyph: "jacket" | "boots";
}

export interface IDemoAnswer {
  /** The suggested question, exactly as the visitor sees it on the chip. */
  question: string;
  /** The bot's reply. Rendered word by word to mimic a real stream. */
  answer: string;
  /** Optional product cards rendered under the reply (e-commerce bots). */
  cards?: IDemoProductCard[];
  /** Optional inline action button, e.g. a booking handoff. */
  action?: { label: string };
}

export interface IDemoBot {
  id: string;
  /** Tab label. Kept short so four tabs fit a 390px screen. */
  tab: string;
  /** The fictional site the bot was trained on. */
  siteName: string;
  /** Shown in the chat header under the site name. */
  knowledge: string;
  /** Two-letter avatar. */
  initials: string;
  /** Accent used for the bot's avatar and header tint. */
  accent: string;
  greeting: string;
  /**
   * Plays automatically a moment after the bot loads so the panel is never an
   * empty box. Deliberately not one of the three chips, so the visitor still
   * has all three to try themselves.
   */
  opener: IDemoAnswer;
  answers: IDemoAnswer[];
}

/** Reply when a visitor types their own question instead of tapping a chip. */
export const OFF_SCRIPT_REPLY =
  "That one needs your content, not mine. This demo only knows the sample site above. Build your own in the editor, point it at your URL, and it will answer questions like that from your real pages.";

export const DEMO_BOTS: IDemoBot[] = [
  {
    id: "store",
    tab: "Online store",
    siteName: "Northwind Supply",
    knowledge: "18 pages crawled · 2 files uploaded",
    initials: "NS",
    accent: "#1f6f5c",
    greeting: "Hi, I'm the Northwind assistant. Ask me about gear, shipping or returns.",
    opener: {
      question: "What do you sell?",
      answer:
        "Outdoor gear for hiking and trail running: shells, insulation, boots, packs and base layers. Everything you see on the site is in stock unless the product page says otherwise.",
    },
    answers: [
      {
        question: "Do you ship to Canada?",
        answer:
          "Yes. Standard shipping to Canada is $12 and takes 5 to 8 business days, and it's free on orders over $75. Duties are calculated at checkout so nothing lands as a surprise later.",
      },
      {
        question: "What's your return policy?",
        answer:
          "30 days from delivery, unworn and with tags still on. Returns inside the US are free. Canadian returns cost $8, taken off the refund.",
      },
      {
        question: "Waterproof jackets under $150?",
        answer: "Two in stock right now under $150, both fully seam-sealed:",
        cards: [
          {
            title: "Cascade Shell 2L",
            price: "$139",
            meta: "Waterproof · 4 colours",
            glyph: "jacket",
          },
          {
            title: "Fen Rain Jacket",
            price: "$118",
            meta: "Packable · 3 colours",
            glyph: "boots",
          },
        ],
      },
    ],
  },
  {
    id: "docs",
    tab: "Help centre",
    siteName: "Meridian",
    knowledge: "96 help articles crawled",
    initials: "MD",
    accent: "#3949c4",
    greeting: "Hi. I'm trained on the Meridian help centre. What are you stuck on?",
    opener: {
      question: "What is Meridian?",
      answer:
        "Meridian is a workspace for small teams: tasks, docs and time tracking in one place. I'm trained on the whole help centre, so ask me anything about setting it up or using it day to day.",
    },
    answers: [
      {
        question: "How do I reset my password?",
        answer:
          "Open the sign-in page, choose Forgot password, and enter the email on the account. The reset link is good for one hour. If it doesn't arrive, check spam first, then confirm you're using the email the account was created with.",
      },
      {
        question: "Can I invite my team?",
        answer:
          "Yes. Settings, then Members, then Invite. Every plan above Free includes 5 seats, and extra seats are billed monthly. Invited members land as Editors by default and you can change the role before or after they accept.",
      },
      {
        question: "Do you have an API?",
        answer:
          "There's a REST API covering projects, members and exports. You'll need a personal access token from Settings, then Developer. Rate limit is 600 requests a minute per token.",
      },
    ],
  },
  {
    id: "course",
    tab: "Online course",
    siteName: "Atlas Academy",
    knowledge: "Course outline + 4 PDFs uploaded",
    initials: "AA",
    accent: "#a8541f",
    greeting: "Hello. I can answer anything about the Atlas photography course.",
    opener: {
      question: "What is this course?",
      answer:
        "Atlas Photography takes you from automatic mode to shooting confidently in manual: exposure, composition, light and a full editing workflow. Self-paced, and you keep access for good.",
    },
    answers: [
      {
        question: "How long is the course?",
        answer:
          "42 lessons, about 11 hours of video, and most students finish in six weeks at two lessons a week. Access doesn't expire, so you can take longer without losing anything.",
      },
      {
        question: "Do I get a certificate?",
        answer:
          "Yes, once you've completed every module and submitted the final portfolio. It's issued as a PDF with a verification link you can put on LinkedIn.",
      },
      {
        question: "What gear do I need?",
        answer:
          "Any camera with manual mode, including a phone that supports it. Module 3 onwards assumes you can set aperture and shutter speed yourself. No lighting kit needed until module 7, and that module has a daylight-only alternative.",
      },
    ],
  },
  {
    id: "local",
    tab: "Local business",
    siteName: "Rivera Dental",
    knowledge: "9 pages crawled · opening hours added as text",
    initials: "RD",
    accent: "#0f6c8c",
    greeting: "Hi there. I can help with hours, insurance and booking.",
    opener: {
      question: "Where are you based?",
      answer:
        "We're at 214 Rivera Street, two blocks from the Lincoln Park stop, with free parking behind the building. The entrance is step-free.",
    },
    answers: [
      {
        question: "Are you open on Saturdays?",
        answer:
          "Yes, 9am to 2pm, for cleanings and emergencies only. Weekday hours are 8am to 6pm, Monday through Thursday, and 8am to 4pm on Friday.",
      },
      {
        question: "Which insurance do you take?",
        answer:
          "We're in network with Delta Dental, Cigna and MetLife, and we'll file out-of-network claims for you with most other providers. Bring your card to the first visit and the front desk will check coverage before any work starts.",
      },
      {
        question: "How do I book a cleaning?",
        answer:
          "I can start that for you now. A standard cleaning takes 45 minutes and the next openings are Thursday afternoon and Saturday morning.",
        action: { label: "Book a cleaning" },
      },
    ],
  },
];
